// Screen 1 - add-app type picker (slice-2-onboarding.md §4). Captures type (+ origin for
// Imported) into local wizard state only; the App record is created once intent is also
// known (IntentStep) - see src/state/wizard.tsx's header comment for the cited,
// resolved ordering conflict with the spec's flow diagram.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AppOrigin, AppType } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { OuterStepper } from "../../components/Stepper.js";
import { DeniedCard } from "../../components/DeniedCard.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";

const TYPE_OPTIONS: readonly {
  readonly type: AppType;
  readonly title: string;
  readonly description: string;
}[] = [
  {
    type: "existing",
    title: "Existing",
    description:
      "Anything already in a Git repo - an app, an automation, a script, a background job. Not just apps with a screen.",
  },
  {
    type: "imported",
    title: "Imported",
    description:
      "Built in a tool like Lovable, Bolt, Replit, v0, or Cursor, and not in a repo yet. We bring it into one.",
  },
  {
    type: "new",
    title: "New",
    description: "An idea, no code yet. Glim helps you shape it before anything gets built.",
  },
];

const ORIGIN_OPTIONS: readonly { readonly value: AppOrigin; readonly label: string }[] = [
  { value: "lovable", label: "Lovable" },
  { value: "bolt", label: "Bolt" },
  { value: "replit", label: "Replit" },
  { value: "v0", label: "v0" },
  { value: "cursor", label: "Cursor" },
];

export const TypePicker = (): React.JSX.Element => {
  const { state: session } = useSession();
  const { setType } = useWizard();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<AppType | null>(null);
  const [origin, setOrigin] = useState<AppOrigin | null>(null);

  if (!isOwnerOrAdmin(session)) {
    return (
      <FocusedFlowShell title="Add an app">
        <DeniedCard message="Ask an owner or admin on this account, or ask them to make you an admin." />
      </FocusedFlowShell>
    );
  }

  if (selected === "new") {
    return (
      <FocusedFlowShell
        title="Add an app"
        onBack={() => {
          setSelected(null);
        }}
      >
        <div className="card state-card">
          <div className="h3">Talk to Glim is coming in a later release</div>
          <p className="muted">We will let you know the moment it is ready.</p>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              void navigate("/apps");
            }}
          >
            Back
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const canContinue = selected === "existing" || (selected === "imported" && origin !== null);

  const handleContinue = (): void => {
    if (selected === null) return;
    const resolvedOrigin: AppOrigin = selected === "existing" ? "own_repo" : (origin ?? "own_repo");
    setType(selected, resolvedOrigin);
    void navigate("/apps/new/intent");
  };

  return (
    <FocusedFlowShell
      title="Add an app"
      onBack={() => {
        void navigate("/apps");
      }}
      cta={
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canContinue}
          onClick={handleContinue}
        >
          Continue
        </button>
      }
    >
      <OuterStepper active="connect" />
      <div className="grid mt24" style={{ gap: 12 }}>
        {TYPE_OPTIONS.map((option) => (
          <button
            key={option.type}
            type="button"
            className="card pad type-card"
            aria-pressed={selected === option.type}
            onClick={() => {
              setSelected(option.type);
            }}
          >
            <div className="row aic gap8">
              <span className="h3">{option.title}</span>
              {selected === option.type ? (
                <span className="check" aria-hidden="true">
                  ✓
                </span>
              ) : null}
            </div>
            <p className="muted">{option.description}</p>
          </button>
        ))}
      </div>
      {selected === "imported" ? (
        <fieldset className="mt16" style={{ border: "none", padding: 0 }}>
          <legend className="field-label">Where was it built?</legend>
          <div className="row gap8" style={{ flexWrap: "wrap" }}>
            {ORIGIN_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="chip"
                aria-pressed={origin === opt.value}
                style={
                  origin === opt.value
                    ? { borderColor: "var(--amber-line)", background: "var(--amber-soft)" }
                    : undefined
                }
                onClick={() => {
                  setOrigin(opt.value);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
    </FocusedFlowShell>
  );
};
